import xss from 'xss';
import type { TFunction } from 'i18next';
import { CustomTextOptions } from 'interfaces/WidgetConfigs.interface';
import { parseTemplate } from 'utils/template';

export enum CustomTextDefault {
  PendingApproval = 'pendingApproval',
  UpperRejection = 'upperRejection',
  BookingUnsuccessful = 'bookingUnsuccessful',
  OrderNonEligible = 'orderNonEligible',
  // ARTEMIS-6557
  LabelDownloadCta = 'labelDownloadCta',
}

export const getDefaultCustomText = (key: CustomTextDefault, t: TFunction) => {
  if (!key) return '';

  const DEFAULT_CUSTOM_TEXT = {
    [CustomTextDefault.PendingApproval]: t(
      'RETURN_PENDING_APPROVAL_DEFAULT_TEXT',
    ),
    [CustomTextDefault.UpperRejection]: t('RETURN_UPPER_REJECTION_TEXT'),
    [CustomTextDefault.BookingUnsuccessful]: t(
      'RETURN_CARRIER_BOOKING_UNSUCCESSFUL',
    ),
    [CustomTextDefault.OrderNonEligible]: t('RETURN_ORDER_NON_ELIGIBLE'),
    [CustomTextDefault.LabelDownloadCta]: t('RETURN_DOWNLOAD_LABEL_QR'),
  };

  return DEFAULT_CUSTOM_TEXT[key] || '';
};

const parseLegacyCustomText = (
  customText: CustomTextOptions,
  defaultText: string,
) => {
  let { text } = customText;
  // for both cases empty string and undefined
  text = text || defaultText;

  const { hyperText, hyperLink } = customText;

  if (!text) return '';

  if (!hyperLink) {
    return text;
  }

  const hyperlinkTemplate = `<a href='{{ hyperLink }}' target='_blank'>{{ hyperText if hyperText else hyperLink }}</a>`;

  const parsedHyperLink = parseTemplate(hyperlinkTemplate, {
    hyperText,
    hyperLink,
  });

  return parseTemplate(text, {
    hyperlink: parsedHyperLink,
  });
};

/**
 * Adds aria-label to anchor tags in HTML string by combining anchor text content
 * with additional dynamic text. Only adds aria-label if one doesn't already exist.
 *
 * @param html - HTML string containing anchor tags
 * @param additionalText - Additional text to append to the anchor's text content for aria-label
 * @returns HTML string with aria-labels added to anchor tags
 */
export const addAriaLabelToAnchors = (
  html: string,
  additionalText: string | undefined,
): string => {
  if (!html || !additionalText) {
    return html;
  }

  return html.replace(
    /<a([^>]*)>(.*?)<\/a>/gi,
    (match, attributes, textContent) => {
      // Check if aria-label already exists (respect existing values)
      if (/aria-label\s*=/i.test(attributes)) {
        return match;
      }

      // Extract text content and strip HTML tags for aria-label
      const cleanText = textContent.replace(/<[^>]*>/g, '').trim();

      // If no text content, return original (nothing meaningful to label)
      if (!cleanText) {
        return match;
      }

      // Create aria-label combining anchor text with additional text
      const ariaLabel = `${cleanText} ${additionalText}`;

      // Escape quotes in aria-label value
      const escapedAriaLabel = ariaLabel.replace(/"/g, '&quot;');

      // Add aria-label to anchor tag
      return `<a${attributes} aria-label="${escapedAriaLabel}">${textContent}</a>`;
    },
  );
};

export const getCustomTextFactory = (t: TFunction) => {
  return (
    customText: string | CustomTextOptions,
    defaultText: CustomTextDefault,
    options: Record<string, any>,
  ) => {
    const translatedDefaultText = getDefaultCustomText(defaultText, t);

    if (typeof customText === 'string') {
      // Add target and style to anchor tags
      let text = customText.replace(/<a/g, `<a target='_blank'`);

      text = parseTemplate(text || translatedDefaultText, options);

      text = xss(text, {
        whiteList: {
          a: ['href', 'target', 'style', 'aria-label'],
          b: [],
          div: ['style'],
          br: [],
        },
        stripIgnoreTag: true,
        stripIgnoreTagBody: ['script'],
      });

      return text;
    }

    return parseLegacyCustomText(customText, translatedDefaultText);
  };
};
